import React, { useEffect, useRef, useState } from "react";
import { getAssistantStatus, assistantChat } from "../../api/client";

/**
 * Floating in-app help assistant. Answers "how do I use Regreports" questions
 * (how-to only — it has no access to program data). Renders nothing unless the
 * server reports the assistant is configured (ANTHROPIC_API_KEY set), so an
 * unconfigured deployment shows no broken UI.
 */
type Msg = { role: "user" | "assistant"; content: string };

const GREETING: Msg = {
  role: "assistant",
  content: "Hi! I can help you use Regreports — ask me things like “how do I add an industrial user?” or “where do I run the annual report?”",
};

const AssistantWidget: React.FC = () => {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getAssistantStatus()
      .then(r => setEnabled(!!r.data?.enabled))
      .catch(() => setEnabled(false));
  }, []);

  useEffect(() => {
    if (open && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, sending]);

  if (!enabled) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setError("");
    const next: Msg[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      // Send only the real conversation (skip the canned greeting).
      const convo = next.filter(m => m !== GREETING).map(m => ({ role: m.role, content: m.content }));
      const r = await assistantChat(convo);
      setMessages(m => [...m, { role: "assistant", content: r.data.reply }]);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? "The assistant is unavailable right now.");
    } finally {
      setSending(false);
    }
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      {open && (
        <div style={s.panel} role="dialog" aria-label="Regreports help assistant">
          <div style={s.header}>
            <span style={{ fontWeight: 700 }}>Help assistant</span>
            <button style={s.close} onClick={() => setOpen(false)} aria-label="Close">×</button>
          </div>
          <div style={s.body} ref={scrollRef}>
            {messages.map((m, i) => (
              <div key={i} style={{ ...s.row, justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
                <div style={m.role === "user" ? s.userBubble : s.botBubble}>{m.content}</div>
              </div>
            ))}
            {sending && <div style={s.row}><div style={s.botBubble}><em style={{ color: "#718096" }}>…thinking</em></div></div>}
            {error && <div style={{ ...s.row }}><div style={{ ...s.botBubble, color: "#c53030" }}>{error}</div></div>}
          </div>
          <div style={s.inputRow}>
            <textarea
              style={s.input}
              value={input}
              placeholder="Ask how to do something…"
              rows={2}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKey}
            />
            <button style={s.send} onClick={send} disabled={sending || !input.trim()}>Send</button>
          </div>
          <div style={s.disclaimer}>Answers explain how to use the app. It can’t see your program’s data.</div>
        </div>
      )}
      <button style={s.launcher} onClick={() => setOpen(o => !o)} aria-label="Help assistant">
        {open ? "×" : "?"}
      </button>
    </>
  );
};

const s: Record<string, React.CSSProperties> = {
  launcher: {
    position: "fixed", right: 24, bottom: 24, zIndex: 1000,
    width: 52, height: 52, borderRadius: "50%", border: "none",
    background: "#3182ce", color: "#fff", fontSize: 24, fontWeight: 700,
    cursor: "pointer", boxShadow: "0 4px 14px rgba(0,0,0,.25)",
  },
  panel: {
    position: "fixed", right: 24, bottom: 88, zIndex: 1000,
    width: 360, maxWidth: "calc(100vw - 32px)", height: 480, maxHeight: "70vh",
    display: "flex", flexDirection: "column",
    background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12,
    boxShadow: "0 10px 30px rgba(0,0,0,.2)", overflow: "hidden",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "12px 14px", borderBottom: "1px solid #edf2f7", background: "#1a365d", color: "#fff",
  },
  close: { background: "transparent", border: "none", color: "#fff", fontSize: 22, cursor: "pointer", lineHeight: 1 },
  body: { flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 8, background: "#f7fafc" },
  row: { display: "flex" },
  userBubble: { background: "#3182ce", color: "#fff", borderRadius: "12px 12px 2px 12px", padding: "8px 11px", fontSize: 13.5, maxWidth: "82%", whiteSpace: "pre-wrap" },
  botBubble: { background: "#fff", color: "#2d3748", border: "1px solid #e2e8f0", borderRadius: "12px 12px 12px 2px", padding: "8px 11px", fontSize: 13.5, maxWidth: "82%", whiteSpace: "pre-wrap" },
  inputRow: { display: "flex", gap: 8, padding: 10, borderTop: "1px solid #edf2f7", alignItems: "flex-end" },
  input: { flex: 1, resize: "none", border: "1px solid #cbd5e0", borderRadius: 8, padding: "8px 10px", fontSize: 13.5, fontFamily: "inherit" },
  send: { background: "#3182ce", color: "#fff", border: "none", borderRadius: 8, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  disclaimer: { fontSize: 11, color: "#a0aec0", textAlign: "center", padding: "0 10px 8px" },
};

export default AssistantWidget;
